import React, { useState, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  type ActionMenuItem, SyutGrid, type SyutGridColumn, type SelectOption,
} from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { bankStatementService, receiptService, paymentModeService } from 'api/services';
import { SyutSelect } from 'components/ui';
import type {
  BankStatementDto, BankStatementLineDto, BankStatementLineQueryParams,
  BankStatementStatus, BankLineMatchStatus, PaginationMeta,
  CreateReceiptRequest, ReceiptDto, BookEntryDto, MatchSuggestionDto, StatementMatchDto, ChargeRuleDto,
} from 'api/types';
import { ReceiptFormModal, ReceiptViewModal } from './ReceiptFormModal';
import { useLedgerLoader } from './ChargeRulesModal';
import { ReceiptVoucherFormModal } from '../accounts/ReceiptVoucherFormModal';
import { PaymentVoucherFormModal } from '../accounts/PaymentVoucherFormModal';
import { usePaymentModeOptions } from 'hooks/usePaymentModeOptions';
import type { StatementLinePrefill } from 'api/types';

// ============================================================================
// RECONCILIATION WORKSHEET (ADR-0055 §4) — an IN-SCREEN view rendered by
// BankStatementScreen inside the module content area (NOT a modal: ActionMenu
// portals live at zIndex 9999, below --z-modal, so row menus would open behind
// a full-screen modal). Deposit AND withdrawal lines are matched to bank-book
// entries (any document that posted to the bank), a deposit can become a
// Treasury receipt, and any line can be ignored. Work in as many sittings as
// needed — nothing has to be finished in one go.
// Its child dialogs stay modals — they stack above the screen normally.
// ============================================================================

export const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (s: string, n: number) => { const d = new Date(`${s.slice(0, 10)}T00:00:00`); d.setDate(d.getDate() + n); return isoDay(d); };

export const lineAmount = (l: BankStatementLineDto) => (l.crAmount > 0 ? l.crAmount : l.drAmount);
export const lineDirection = (l: BankStatementLineDto) => (l.crAmount > 0 ? 'DEPOSIT' as const : 'WITHDRAWAL' as const);

export const statementStatusLabel = (s: BankStatementStatus): string =>
  s === 'PARTIALLY_MATCHED' ? 'In progress' : s === 'COMPLETED' ? 'Reconciled' : 'Imported';

export const statementStatusBadge = (s: BankStatementStatus): React.ReactNode => {
  const style: React.CSSProperties | undefined =
    s === 'PARTIALLY_MATCHED' ? { background: '#d97706', color: '#fff' }
    : s === 'COMPLETED' ? { background: '#16a34a', color: '#fff' }
    : undefined;
  return <span className="badge" style={style}>{statementStatusLabel(s)}</span>;
};

export const matchStatusBadge = (s: BankLineMatchStatus): React.ReactNode => {
  const map: Record<BankLineMatchStatus, { label: string; style?: React.CSSProperties }> = {
    UNMATCHED: { label: 'Unmatched', style: { background: '#d97706', color: '#fff' } },
    MATCHED: { label: 'Matched', style: { background: '#16a34a', color: '#fff' } },
    AUTO_MATCHED: { label: 'Matched', style: { background: '#16a34a', color: '#fff' } },
    MANUAL_MATCHED: { label: 'Matched', style: { background: '#16a34a', color: '#fff' } },
    POSTED: { label: 'Posted', style: { background: '#2563eb', color: '#fff' } },
    RECEIPT_CREATED: { label: 'Receipt Created', style: { background: '#2563eb', color: '#fff' } },
    IGNORED: { label: 'Ignored', style: { background: '#6b7280', color: '#fff' } },
  };
  const m = map[s] ?? { label: s };
  return <span className="badge" style={m.style}>{m.label}</span>;
};

export const MATCH_STATUS_OPTIONS: SelectOption[] = [
  { value: 'UNMATCHED', label: 'Unmatched' },
  { value: 'MATCHED', label: 'Matched' },
  { value: 'RECEIPT_CREATED', label: 'Receipt created' },
  { value: 'IGNORED', label: 'Ignored' },
  { value: '', label: 'All statuses' },
];

/** Bank-statement perspective: DEPOSIT = money in (crAmount), WITHDRAWAL = money out (drAmount). */
export const DIRECTION_OPTIONS: SelectOption[] = [
  { value: '', label: 'Deposits and withdrawals' },
  { value: 'DEPOSIT', label: 'Deposits' },
  { value: 'WITHDRAWAL', label: 'Withdrawals' },
];

/** Line filters — owned by BankStatementScreen's shell chrome (filter panel). */
export interface WorksheetLineFilters {
  /** '' = all statuses. */
  matchStatus: string;
  /** DEPOSIT | WITHDRAWAL | '' (all). */
  direction: string;
  fromDate: string;
  toDate: string;
  minAmount: string;
  maxAmount: string;
  reference: string;
  description: string;
}

/** Defaults per entry mode: reconcile = unmatched lines, both directions; view = everything. */
export const worksheetDefaultFilters = (mode: 'reconcile' | 'view'): WorksheetLineFilters => ({
  matchStatus: mode === 'view' ? '' : 'UNMATCHED',
  direction: '',
  fromDate: '', toDate: '', minAmount: '', maxAmount: '', reference: '', description: '',
});

const DEFAULT_PAGINATION: PaginationMeta = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0, hasNextPage: false, hasPreviousPage: false,
};

const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '0.45rem 0.5rem' };
const td: React.CSSProperties = { padding: '0.3rem 0.5rem' };
const band: React.CSSProperties = { display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.8125rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem' };

const directionWord = (d: 'DEPOSIT' | 'WITHDRAWAL') => (d === 'DEPOSIT' ? 'Deposit' : 'Withdrawal');

/** One book entry as a compact cell: voucher no · party · cheque/UTR. */
const EntryCell: React.FC<{ e: BookEntryDto }> = ({ e }) => (
  <div>
    <strong>{e.transactionNo}</strong>
    <span style={muted}> · {e.voucherTypeName || e.voucherClass}</span>
    {(e.partyName || e.instrumentNo) && (
      <div style={{ ...muted, fontSize: '0.75rem' }}>
        {[e.partyName, e.instrumentNo ? `No. ${e.instrumentNo}` : null].filter(Boolean).join(' · ')}
      </div>
    )}
    {e.narration && <div style={{ ...muted, fontSize: '0.72rem', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.narration}>{e.narration}</div>}
  </div>
);

// ============================================================================
// MATCH DIALOG — one line to one or more entries, or several lines to one entry.
// ============================================================================

interface MatchLinesModalProps {
  isOpen: boolean;
  lines: BankStatementLineDto[];
  bankLedgerId: string;
  onMatched: () => void;
  onClose: () => void;
}

const MatchLinesModal: React.FC<MatchLinesModalProps> = ({ isOpen, lines, bankLedgerId, onMatched, onClose }) => {
  const direction = lines.length ? lineDirection(lines[0]) : 'DEPOSIT';
  const lineTotal = useMemo(() => lines.reduce((s, l) => s + lineAmount(l), 0), [lines]);
  const manyLines = lines.length > 1;

  const [suggestion, setSuggestion] = useState<MatchSuggestionDto | null>(null);
  const [entries, setEntries] = useState<BookEntryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selected, setSelected] = useState<Map<string, BookEntryDto>>(new Map());
  const [fromSuggestion, setFromSuggestion] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  // Charges (ADR-0055 §6): the difference booked at confirmation, and settlements from a clearing account.
  const [rules, setRules] = useState<ChargeRuleDto[]>([]);
  const [source, setSource] = useState('');          // '' = the bank itself
  const [chargesLedgerId, setChargesLedgerId] = useState('');
  const [gstLedgerId, setGstLedgerId] = useState('');
  const [gstText, setGstText] = useState('');
  const ledgerLabels = React.useRef<Map<string, string>>(new Map());
  const loadLedger = useLedgerLoader(ledgerLabels);

  useEffect(() => {
    if (!isOpen || lines.length === 0) return;
    const dates = lines.map(l => l.txnDate.slice(0, 10)).sort();
    setFromDate(addDays(dates[0], direction === 'WITHDRAWAL' ? -60 : -10));
    setToDate(addDays(dates[dates.length - 1], 3));
    setSearch(''); setSearchApplied(''); setSelected(new Map()); setFromSuggestion(false); setRemarks(''); setSuggestion(null);
    setSource(''); setChargesLedgerId(''); setGstLedgerId(''); setGstText('');
    if (lines.length === 1) {
      bankStatementService.getLineSuggestion(lines[0].id).then(setSuggestion).catch(() => setSuggestion(null));
    }
    paymentModeService.getRules(bankLedgerId).then(setRules).catch(() => setRules([]));
  }, [isOpen, lines, direction, bankLedgerId]);

  const clearingSources = useMemo(() => {
    const m = new Map<string, string>();
    rules.filter(r => r.settlementMode === 'BATCH' && r.sourceLedgerId).forEach(r => m.set(r.sourceLedgerId!, r.sourceLedgerName || 'Clearing account'));
    return Array.from(m.entries()).map(([value, label]) => ({ value, label }));
  }, [rules]);

  useEffect(() => {
    if (!isOpen || !bankLedgerId || !fromDate) return;
    let cancelled = false;
    setLoading(true);
    bankStatementService.getBookEntries({
      bankLedgerId: source || bankLedgerId, direction, fromDate, toDate, searchTerm: searchApplied || undefined, unreconciledOnly: true, page: 1, pageSize: 100,
    })
      .then(res => { if (!cancelled) { setEntries(res.data); setTotal(res.pagination.totalItems); } })
      .catch((err: any) => { if (!cancelled) notificationService.toast.error(err?.response?.data?.message || 'Could not load the bank book.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, bankLedgerId, source, direction, fromDate, toDate, searchApplied]);

  const toggle = useCallback((e: BookEntryDto, viaSuggestion = false) => {
    setSelected(prev => {
      const next = new Map(manyLines ? [] : prev);   // several lines → exactly one entry
      if (prev.has(e.entryId)) next.delete(e.entryId); else next.set(e.entryId, e);
      return next;
    });
    setFromSuggestion(viaSuggestion);
  }, [manyLines]);

  const selectedTotal = useMemo(() => Array.from(selected.values()).reduce((s, e) => s + e.amount, 0), [selected]);
  const difference = Math.round((lineTotal - selectedTotal) * 100) / 100;

  // A deposit credited short, or a withdrawal debited more, can be closed by booking the
  // difference as charges; a settlement from a clearing account always books through the journal.
  const deduction = Math.round((direction === 'DEPOSIT' ? selectedTotal - lineTotal : lineTotal - selectedTotal) * 100) / 100;
  const chargesPossible = selected.size > 0 && deduction > 0.004 && deduction <= Math.max(selectedTotal, lineTotal) * 0.25;
  const useCharges = chargesPossible || (!!source && selected.size > 0 && Math.abs(deduction) < 0.005);
  const gstAmount = Math.min(Math.max(0, parseFloat(gstText) || 0), Math.max(0, deduction));
  const chargeAmount = Math.round((deduction - gstAmount) * 100) / 100;

  // Prefill the ledgers (and GST split) from the rule for the first selected entry's mode.
  useEffect(() => {
    if (!chargesPossible || chargesLedgerId) return;
    const first = Array.from(selected.values())[0];
    const mode = first?.instrumentType?.toUpperCase();
    const pick = (bank: string | null, anyMode: boolean) => rules.find(r =>
      (r.bankLedgerId ?? null) === bank && (anyMode ? !r.paymentModeId : !!mode && r.modeCode === mode));
    const rule = (mode ? pick(bankLedgerId, false) ?? pick(null, false) : undefined) ?? pick(bankLedgerId, true) ?? pick(null, true);
    if (!rule) return;
    if (rule.chargesLedgerId) { setChargesLedgerId(rule.chargesLedgerId); ledgerLabels.current.set(rule.chargesLedgerId, rule.chargesLedgerName || ''); }
    if (rule.gstLedgerId && rule.gstPercent) {
      setGstLedgerId(rule.gstLedgerId); ledgerLabels.current.set(rule.gstLedgerId, rule.gstLedgerName || '');
      setGstText(String(Math.round(deduction * rule.gstPercent / (100 + rule.gstPercent) * 100) / 100));
    }
  }, [chargesPossible, chargesLedgerId, selected, rules, bankLedgerId, deduction]);

  const chargesValid = !useCharges || ((chargeAmount <= 0.004 || !!chargesLedgerId) && (gstAmount <= 0.004 || !!gstLedgerId));
  const canConfirm = selected.size > 0 && (Math.abs(difference) < 0.005 || chargesPossible) && chargesValid && !saving;

  const handleConfirm = useCallback(async () => {
    setSaving(true);
    try {
      const { result } = await bankStatementService.confirmMatches([{
        lineIds: lines.map(l => l.id),
        entryIds: Array.from(selected.keys()),
        method: fromSuggestion ? 'SUGGESTED' : 'MANUAL',
        tier: fromSuggestion ? suggestion?.tier ?? null : null,
        remarks: remarks.trim() || null,
        ...(useCharges ? {
          chargeAmount: Math.max(0, chargeAmount), gstAmount,
          chargesLedgerId: chargeAmount > 0 ? chargesLedgerId || null : null,
          gstLedgerId: gstAmount > 0 ? gstLedgerId || null : null,
          sourceLedgerId: source || null,
        } : {}),
      }]);
      if (result.failures.length > 0) {
        notificationService.toast.error(result.failures[0].message);
        return;
      }
      notificationService.toast.success('Matched.');
      onMatched();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not match.');
    } finally {
      setSaving(false);
    }
  }, [lines, selected, fromSuggestion, suggestion, remarks, useCharges, chargeAmount, gstAmount, chargesLedgerId, gstLedgerId, source, onMatched, onClose]);

  if (!isOpen || lines.length === 0) return null;

  const entryRow = (e: BookEntryDto, viaSuggestion: boolean, extra?: React.ReactNode) => {
    const on = selected.has(e.entryId);
    return (
      <tr key={(viaSuggestion ? 's-' : 'b-') + e.entryId} onClick={() => toggle(e, viaSuggestion)}
        style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer', background: on ? 'rgba(37,99,235,0.08)' : undefined }}>
        <td style={{ ...td, width: 32, textAlign: 'center' }}>
          <input type={manyLines ? 'radio' : 'checkbox'} checked={on} readOnly />
        </td>
        <td style={{ ...td, width: 105 }}>{fmtDate(e.transactionDate)}</td>
        <td style={td}><EntryCell e={e} /></td>
        <td style={{ ...td, width: 120, textAlign: 'right', fontWeight: 600 }}>{money(e.amount)}</td>
        {extra !== undefined && <td style={{ ...td, width: 230, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{extra}</td>}
      </tr>
    );
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{manyLines ? `Match ${lines.length} Lines` : `Match ${directionWord(direction)}`}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {/* The statement side */}
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', marginBottom: '0.75rem', maxHeight: 150, overflow: 'auto' }}>
            <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <th style={{ ...th, width: 105 }}>Bank date</th><th style={th}>Description</th><th style={{ ...th, width: 150 }}>Reference</th>
                <th style={{ ...th, width: 120, textAlign: 'right' }}>{directionWord(direction)}</th>
              </tr></thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={td}>{fmtDate(l.txnDate)}</td><td style={td}>{l.description || '—'}</td><td style={td}>{l.reference || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(lineAmount(l))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Suggested (single line) */}
          {!manyLines && suggestion && suggestion.candidates.length > 0 && (
            <>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', margin: '0.25rem 0 0.35rem' }}>
                Suggested {suggestion.tier === 'HIGH' ? <span className="badge" style={{ background: '#16a34a', color: '#fff' }}>Confident</span> : <span className="badge" style={{ background: '#d97706', color: '#fff' }}>Check</span>}
                {suggestion.note && <span style={{ ...muted, fontWeight: 400 }}> — {suggestion.note}</span>}
              </div>
              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', marginBottom: '0.75rem' }}>
                <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <tbody>{suggestion.candidates.map(c => entryRow(c.entry, true, c.reasons.join(' · ')))}</tbody>
                </table>
              </div>
            </>
          )}

          {/* Search the bank book */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginRight: 'auto' }}>
              {source ? 'Clearing account' : 'Bank book'} — unreconciled {direction === 'DEPOSIT' ? 'money in' : 'money out'} ({total})
              {manyLines && <span style={{ ...muted, fontWeight: 400 }}> — several lines match one entry</span>}
            </div>
            {direction === 'DEPOSIT' && !manyLines && clearingSources.length > 0 && (
              <div style={{ minWidth: 230 }}>
                <SyutSelect label="Receipts from" options={[{ value: '', label: 'This bank account' }, ...clearingSources]}
                  value={source} menuPortalTarget={document.body}
                  onChange={(v) => { setSource((v as string) || ''); setSelected(new Map()); }} isClearable={false} size="sm" />
              </div>
            )}
            <div><label style={labelStyle}>From</label>
              <input type="date" className="form-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.3rem 0.5rem' }} /></div>
            <div><label style={labelStyle}>To</label>
              <input type="date" className="form-input" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.3rem 0.5rem' }} /></div>
            <div style={{ minWidth: 240 }}><label style={labelStyle}>Search</label>
              <input type="text" className="form-input" value={search} placeholder="Voucher no, party, cheque/UTR, amount…"
                onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') setSearchApplied(search.trim()); }}
                onBlur={() => setSearchApplied(search.trim())} style={{ fontSize: '0.8125rem', padding: '0.3rem 0.5rem' }} /></div>
          </div>
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', maxHeight: '32vh', overflow: 'auto' }}>
            <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr style={{ background: 'var(--grid-header-bg, #dbeafe)', position: 'sticky', top: 0 }}>
                <th style={{ ...th, width: 32 }} /><th style={{ ...th, width: 105 }}>Date</th><th style={th}>Voucher</th>
                <th style={{ ...th, width: 120, textAlign: 'right' }}>Amount</th>
              </tr></thead>
              <tbody>
                {loading && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', ...muted }}>Loading…</td></tr>}
                {!loading && entries.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, textAlign: 'center', ...muted, padding: '1rem' }}>
                    Nothing unreconciled in this range. Widen the dates, or post the entry from the line{direction === 'DEPOSIT' ? ' (Create Receipt)' : ''}.
                  </td></tr>
                )}
                {!loading && entries.map(e => entryRow(e, false))}
              </tbody>
            </table>
          </div>

          <div style={{ ...band, marginTop: '0.75rem', marginBottom: 0 }}>
            <span><span style={muted}>Statement </span><strong>{money(lineTotal)}</strong></span>
            <span><span style={muted}>Selected ({selected.size}) </span><strong>{money(selectedTotal)}</strong></span>
            <span><span style={muted}>Difference </span>
              <strong style={{ color: Math.abs(difference) < 0.005 ? '#16a34a' : chargesPossible ? '#d97706' : '#dc2626' }}>{money(difference)}</strong></span>
            <input type="text" className="form-input" value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder="Note (optional)" style={{ marginLeft: 'auto', minWidth: 220, fontSize: '0.8125rem', padding: '0.3rem 0.5rem' }} />
          </div>

          {useCharges && (
            <div style={{ border: '1px solid rgba(217,119,6,0.4)', background: 'rgba(217,119,6,0.06)', borderRadius: '0.5rem', padding: '0.6rem 0.75rem', marginTop: '0.6rem' }}>
              <div style={{ fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                {source
                  ? <>Settlement from the clearing account: the bank received <strong>{money(lineTotal)}</strong> for receipts of <strong>{money(selectedTotal)}</strong>.</>
                  : direction === 'DEPOSIT'
                    ? <>The bank credited <strong>{money(deduction)}</strong> less than the books — book it as charges deducted.</>
                    : <>The bank debited <strong>{money(deduction)}</strong> more than the books — book it as charges.</>}
                <span style={muted}> Booked in a Bank Charges journal; unmatching removes it.</span>
              </div>
              {deduction > 0.004 && (
                <div className="form-grid-4" style={{ alignItems: 'flex-end' }}>
                  <div>
                    <label style={labelStyle}>Charge</label>
                    <input className="form-input" value={money(Math.max(0, chargeAmount))} disabled style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem', textAlign: 'right' }} />
                  </div>
                  <div>
                    <SyutSelect label={<>Charges ledger <span className="text-danger">*</span></>} isAsync loadOptions={loadLedger} defaultOptions cacheOptions={false}
                      menuPortalTarget={document.body}
                      seedOptions={chargesLedgerId ? [{ value: chargesLedgerId, label: ledgerLabels.current.get(chargesLedgerId) || '' }] : undefined}
                      value={chargesLedgerId} onChange={(v) => setChargesLedgerId((v as string) || '')} isClearable size="sm" />
                  </div>
                  <div>
                    <label style={labelStyle}>GST on the charge</label>
                    <input className="form-input" type="number" step="0.01" value={gstText} onChange={(e) => setGstText(e.target.value)}
                      placeholder="0.00" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem', textAlign: 'right' }} />
                  </div>
                  <div>
                    <SyutSelect label="GST ledger (input tax)" isAsync loadOptions={loadLedger} defaultOptions cacheOptions={false}
                      menuPortalTarget={document.body}
                      seedOptions={gstLedgerId ? [{ value: gstLedgerId, label: ledgerLabels.current.get(gstLedgerId) || '' }] : undefined}
                      value={gstLedgerId} onChange={(v) => setGstLedgerId((v as string) || '')} isClearable size="sm" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConfirm} disabled={!canConfirm}
            title={selected.size > 0 && Math.abs(difference) >= 0.005 && !chargesPossible ? 'The totals must agree' : !chargesValid ? 'Choose the ledgers for the charges' : undefined}>
            <IconSystem.link size={16} /> {saving ? 'Matching…' : useCharges && deduction > 0.004 ? `Match and book ${money(deduction)} charges` : 'Match'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// MATCH DETAILS — both sides of a standing match, with Unmatch.
// ============================================================================

interface MatchDetailsModalProps {
  isOpen: boolean;
  line: BankStatementLineDto | null;
  canUpdate: boolean;
  onUnmatched: () => void;
  onClose: () => void;
}

const MatchDetailsModal: React.FC<MatchDetailsModalProps> = ({ isOpen, line, canUpdate, onUnmatched, onClose }) => {
  const [match, setMatch] = useState<StatementMatchDto | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen || !line) { setMatch(null); return; }
    bankStatementService.getLineMatch(line.id).then(setMatch).catch(() => setMatch(null));
  }, [isOpen, line]);

  const handleUnmatch = useCallback(async () => {
    if (!line) return;
    setBusy(true);
    try {
      await bankStatementService.unmatchLine(line.id);
      notificationService.toast.success('Unmatched — the lines and entries are open again.');
      setConfirmOpen(false);
      onUnmatched();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not unmatch.');
    } finally {
      setBusy(false);
    }
  }, [line, onUnmatched, onClose]);

  if (!isOpen || !line) return null;
  const posted = match?.matchMethod === 'RECEIPT_CREATED' || match?.matchMethod === 'POSTED';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Matched {match ? directionWord(match.direction).toLowerCase() : ''}</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {!match && <div style={{ ...muted, textAlign: 'center', padding: '1rem' }}>Loading…</div>}
          {match && (
            <>
              <div style={band}>
                <span><span style={muted}>Cleared at the bank </span><strong>{fmtDate(match.clearedDate)}</strong></span>
                <span><span style={muted}>Statement </span><strong>{money(match.lineTotal)}</strong></span>
                <span><span style={muted}>Books </span><strong>{money(match.entryTotal)}</strong></span>
                {match.chargeTotal > 0 && <span><span style={muted}>Charges </span><strong>{money(match.chargeTotal)}</strong></span>}
                <span><span style={muted}>By </span><strong>{match.createdByName || '—'}</strong> <span style={muted}>{fmtDate(match.createdAt)}</span></span>
              </div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Statement line(s)</div>
              <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
                <tbody>
                  {match.lines.map(l => (
                    <tr key={l.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ ...td, width: 105 }}>{fmtDate(l.txnDate)}</td>
                      <td style={td}>{l.description || '—'}{l.reference && <span style={muted}> · {l.reference}</span>}</td>
                      <td style={{ ...td, width: 120, textAlign: 'right', fontWeight: 600 }}>{money(lineAmount(l))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Bank-book entry(ies)</div>
              <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <tbody>
                  {match.entries.map(e => (
                    <tr key={e.entryId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ ...td, width: 105 }}>{fmtDate(e.transactionDate)}</td>
                      <td style={td}><EntryCell e={e} /></td>
                      <td style={{ ...td, width: 120, textAlign: 'right', fontWeight: 600 }}>{money(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {match.remarks && <div style={{ ...muted, fontSize: '0.78rem', marginTop: '0.6rem' }}>Note: {match.remarks}</div>}
              {match.sharedChargesMatchCount > 1 && (
                <div style={{ fontSize: '0.78rem', color: '#d97706', marginTop: '0.6rem' }}>
                  The charges were booked in one Bank Charges journal together with {match.sharedChargesMatchCount - 1} other line(s).
                  Unmatching releases all {match.sharedChargesMatchCount} matches and removes that journal.
                </div>
              )}
              <div style={{ ...muted, fontSize: '0.75rem', marginTop: '0.6rem' }}>
                While matched, these vouchers are locked: they cannot be edited, deleted or cancelled. Unmatch first to change them.
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {canUpdate && match && (
            <button className="btn btn-danger" onClick={() => setConfirmOpen(true)}><IconSystem.xCircle size={16} /> Unmatch</button>
          )}
        </div>
      </div>
      <ConfirmDialog open={confirmOpen} title="Unmatch"
        message={posted
          ? 'Unmatch this line? The receipt created from it stays posted — cancel it separately if it should not exist.'
          : 'Unmatch? The statement line(s) and the book entry(ies) become unreconciled again and the vouchers unlock.'}
        confirmLabel="Unmatch" cancelLabel="Keep" variant="danger"
        loading={busy} loadingMessage="Unmatching…"
        onConfirm={handleUnmatch} onCancel={() => setConfirmOpen(false)} />
    </>
  );
};

// ============================================================================
// IGNORE DIALOG — a line that will never be matched to the books.
// ============================================================================

interface IgnoreLineDialogProps {
  isOpen: boolean;
  line: BankStatementLineDto | null;
  onIgnored: () => void;
  onClose: () => void;
}

const IgnoreLineDialog: React.FC<IgnoreLineDialogProps> = ({ isOpen, line, onIgnored, onClose }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (isOpen) { setReason(''); setSaving(false); } }, [isOpen]);

  const handleIgnore = useCallback(async () => {
    if (!line) return;
    setSaving(true);
    try {
      await bankStatementService.ignoreLine(line.id, { rowVersion: line.rowVersion, reason: reason.trim() || undefined });
      notificationService.toast.success('Line ignored.');
      onIgnored();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not ignore the line.');
    } finally {
      setSaving(false);
    }
  }, [line, reason, onIgnored, onClose]);

  if (!isOpen || !line) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Ignore Line</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Leave the {directionWord(lineDirection(line)).toLowerCase()} of <strong>{money(lineAmount(line))}</strong> on {fmtDate(line.txnDate)} ({line.description || 'no description'})
            out of the reconciliation — for example a reversal pair the bank cancelled out. An ignored line can be reopened later.
          </div>
          <label style={labelStyle}>Reason (optional)</label>
          <textarea className="form-textarea" rows={2} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Bank reversal — reversed the same day"
            style={{ width: '100%', fontSize: '0.8125rem' }} />
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleIgnore} disabled={saving}>
            {saving ? 'Saving…' : 'Ignore Line'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// QUICK ENTRY — one-click posting for a line the books have nothing for
// (ADR-0055 §5): a withdrawal to an expense (bank charges, a direct debit), a
// deposit to income (interest). Posts a Payment / Receipt Voucher on the bank
// date for exactly the line's amount and matches it to the line.
// ============================================================================

interface QuickEntryDialogProps {
  isOpen: boolean;
  line: BankStatementLineDto | null;
  onPosted: () => void;
  onClose: () => void;
}

const QuickEntryDialog: React.FC<QuickEntryDialogProps> = ({ isOpen, line, onPosted, onClose }) => {
  const [ledgerId, setLedgerId] = useState('');
  const [narration, setNarration] = useState('');
  const [mode, setMode] = useState('');
  const [saving, setSaving] = useState(false);
  const labels = React.useRef<Map<string, string>>(new Map());
  const loadLedger = useLedgerLoader(labels);
  const modeOptions = usePaymentModeOptions([], mode);

  useEffect(() => {
    if (!isOpen || !line) return;
    setLedgerId(''); setNarration(line.description || ''); setMode(''); setSaving(false);
  }, [isOpen, line]);

  const handlePost = useCallback(async () => {
    if (!line || !ledgerId) return;
    setSaving(true);
    try {
      const m = await bankStatementService.postQuick(line.id, { ledgerId, narration: narration.trim() || null, instrumentType: mode || null });
      notificationService.toast.success(`${m.entries[0]?.transactionNo || 'Entry'} posted and matched.`);
      onPosted();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not post the entry.');
    } finally {
      setSaving(false);
    }
  }, [line, ledgerId, narration, mode, onPosted, onClose]);

  if (!isOpen || !line) return null;
  const deposit = lineDirection(line) === 'DEPOSIT';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Quick Entry — {deposit ? 'Interest / Other Income' : 'Bank Charges / Direct Debit'}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div style={{ ...band }}>
            <span><span style={muted}>Bank date </span><strong>{fmtDate(line.txnDate)}</strong></span>
            <span><span style={muted}>{deposit ? 'Deposit' : 'Withdrawal'} </span><strong>{money(lineAmount(line))}</strong></span>
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Posts a {deposit ? 'Receipt' : 'Payment'} Voucher for exactly this amount, dated on the bank date, to the account you choose — and matches it to the line.
            For anything with a party or several accounts, use <em>Post {deposit ? 'Receipt' : 'Payment'} Voucher…</em> instead.
          </div>
          <div className="form-grid-2">
            <div>
              <SyutSelect label={<>{deposit ? 'Income account' : 'Expense account'} <span className="text-danger">*</span></>} isAsync loadOptions={loadLedger}
                defaultOptions cacheOptions={false} menuPortalTarget={document.body}
                seedOptions={ledgerId ? [{ value: ledgerId, label: labels.current.get(ledgerId) || '' }] : undefined}
                value={ledgerId} onChange={(v) => setLedgerId((v as string) || '')}
                placeholder={deposit ? 'e.g. Interest Received' : 'e.g. Bank Charges'} isClearable size="sm" />
            </div>
            <div>
              <SyutSelect label="Payment mode" options={[{ value: '', label: '— none —' }, ...modeOptions]} value={mode}
                menuPortalTarget={document.body} onChange={(v) => setMode((v as string) || '')} isClearable={false} size="sm" />
            </div>
          </div>
          <label style={{ ...labelStyle, marginTop: '0.6rem' }}>Narration</label>
          <input type="text" className="form-input" value={narration} onChange={(e) => setNarration(e.target.value)}
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem', width: '100%' }} />
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handlePost} disabled={saving || !ledgerId}>
            {saving ? 'Posting…' : 'Post and match'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// LINE DETAILS MODAL — full, untruncated fields for one statement line.
// ============================================================================

interface LineDetailsModalProps {
  isOpen: boolean;
  line: BankStatementLineDto | null;
  onClose: () => void;
}

const LineDetailsModal: React.FC<LineDetailsModalProps> = ({ isOpen, line, onClose }) => {
  if (!isOpen || !line) return null;
  const isDeposit = line.crAmount > 0;
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Line Details — #{line.lineNo}</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem 1rem', fontSize: '0.8125rem' }}>
            <div><div style={muted}>Date</div><strong>{fmtDate(line.txnDate)}</strong></div>
            <div><div style={muted}>Value Date</div><strong>{fmtDate(line.valueDate)}</strong></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={muted}>Description</div>
              <strong style={{ wordBreak: 'break-word' }}>{line.description || '—'}</strong>
            </div>
            <div><div style={muted}>Reference</div><strong style={{ wordBreak: 'break-word' }}>{line.reference || '—'}</strong></div>
            <div><div style={muted}>{isDeposit ? 'Deposit' : 'Withdrawal'}</div><strong>{money(lineAmount(line))}</strong></div>
            <div><div style={muted}>Running Balance</div><strong>{line.runningBalance != null ? money(line.runningBalance) : '—'}</strong></div>
            <div><div style={muted}>Status</div>{matchStatusBadge(line.matchStatus)}</div>
            <div><div style={muted}>Matched with</div><strong>{line.matchedEntries || line.matchedReceiptNo || '—'}</strong></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={muted}>Remarks</div>
              <strong style={{ wordBreak: 'break-word' }}>{line.matchRemarks || '—'}</strong>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// WORKSHEET
// ============================================================================

export interface ReconciliationWorksheetProps {
  statement: BankStatementDto | null;
  /** Applied line filters — owned by the screen's shell chrome (filter panel). */
  filters: WorksheetLineFilters;
  /** Debounced header search — ORs description+reference, ANDed with `filters` server-side. */
  searchTerm: string;
  /** Bump to force a lines + header refetch (header Refresh / suggestions confirmed). */
  refreshKey: number;
  /** UI.BANK-STATEMENT.UPDATE — match / unmatch / ignore. */
  canUpdate: boolean;
  /** UI.BANK-STATEMENT.CREATE — create a receipt from a line. */
  canCreate: boolean;
  onChanged: () => void;
  /** Fired whenever fresh statement detail loads (mount + every refresh) so the
   *  screen header can show live numbers (unmatched count, status, balances). */
  onDetailRefreshed?: (d: BankStatementDto) => void;
}

const MATCHED_STATES: BankLineMatchStatus[] = ['MATCHED', 'POSTED', 'RECEIPT_CREATED', 'AUTO_MATCHED', 'MANUAL_MATCHED'];

export const ReconciliationWorksheet: React.FC<ReconciliationWorksheetProps> = ({
  statement, filters, searchTerm, refreshKey, canUpdate, canCreate, onChanged, onDetailRefreshed,
}) => {
  const [detail, setDetail] = useState<BankStatementDto | null>(null);
  const [lines, setLines] = useState<BankStatementLineDto[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState<BankStatementLineDto[]>([]);

  const [selectedLine, setSelectedLine] = useState<BankStatementLineDto | null>(null);
  const [matchLines, setMatchLines] = useState<BankStatementLineDto[]>([]);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchDetailsOpen, setMatchDetailsOpen] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [createReceiptOpen, setCreateReceiptOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reopenLine, setReopenLine] = useState<BankStatementLineDto | null>(null);
  const [reopening, setReopening] = useState(false);
  const [viewReceipt, setViewReceipt] = useState<ReceiptDto | null>(null);
  // Post from a line (ADR-0055 §5)
  const [postRvLine, setPostRvLine] = useState<BankStatementLineDto | null>(null);
  const [postPvLine, setPostPvLine] = useState<BankStatementLineDto | null>(null);
  const [quickLine, setQuickLine] = useState<BankStatementLineDto | null>(null);

  useEffect(() => {
    setPage(1);
    setSelectedLine(null); setMatchOpen(false); setMatchDetailsOpen(false); setIgnoreOpen(false); setCreateReceiptOpen(false);
    setDetailsOpen(false); setReopenLine(null); setViewReceipt(null); setSelectedRows([]);
  }, [statement?.id]);

  const filtersKey = JSON.stringify(filters);
  useEffect(() => { setPage(1); }, [filtersKey, searchTerm]);

  const onDetailRefreshedRef = React.useRef(onDetailRefreshed);
  onDetailRefreshedRef.current = onDetailRefreshed;

  const refreshHeader = useCallback(async () => {
    if (!statement?.id) return;
    try {
      const d = await bankStatementService.getById(statement.id);
      setDetail(d);
      onDetailRefreshedRef.current?.(d);
    } catch { /* screen header falls back to the passed row */ }
  }, [statement?.id]);

  const fetchLines = useCallback(async () => {
    if (!statement?.id) return;
    setLoading(true);
    try {
      const params: BankStatementLineQueryParams = {
        statementId: statement.id,
        page, pageSize,
        matchStatus: filters.matchStatus || undefined,
        direction: filters.direction || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        minAmount: filters.minAmount !== '' ? parseFloat(filters.minAmount) : undefined,
        maxAmount: filters.maxAmount !== '' ? parseFloat(filters.maxAmount) : undefined,
        reference: filters.reference.trim() || undefined,
        description: filters.description.trim() || undefined,
        searchTerm: searchTerm || undefined,
      };
      const res = await bankStatementService.getLines(params);
      setLines(res.data);
      setPagination(res.pagination);
      setSelectedRows([]);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the statement lines.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statement?.id, page, pageSize, filtersKey, searchTerm, refreshKey]);

  useEffect(() => { fetchLines(); }, [fetchLines]);
  useEffect(() => { if (statement?.id) refreshHeader(); }, [statement?.id, refreshHeader, refreshKey]);

  const refreshAll = useCallback(() => {
    fetchLines();
    refreshHeader();
    onChanged();
  }, [fetchLines, refreshHeader, onChanged]);

  const handleConfirmReopen = useCallback(async () => {
    if (!reopenLine) return;
    setReopening(true);
    try {
      await bankStatementService.unmatchLine(reopenLine.id);
      notificationService.toast.success('Line reopened.');
      setReopenLine(null);
      refreshAll();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not reopen the line.');
    } finally {
      setReopening(false);
    }
  }, [reopenLine, refreshAll]);

  const handleCreateReceiptSubmit = useCallback(async (payload: CreateReceiptRequest): Promise<ReceiptDto> => {
    if (!selectedLine) throw new Error('No statement line selected.');
    return bankStatementService.createReceiptFromLine(selectedLine.id, { rowVersion: selectedLine.rowVersion, receipt: payload });
  }, [selectedLine]);

  const handleViewReceipt = useCallback(async (line: BankStatementLineDto) => {
    if (!line.matchedReceiptId) return;
    try {
      setViewReceipt(await receiptService.getById(line.matchedReceiptId));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the receipt.');
    }
  }, []);

  const openMatch = useCallback((ls: BankStatementLineDto[]) => { setMatchLines(ls); setMatchOpen(true); }, []);

  const getRowActions = useCallback((line: BankStatementLineDto): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'details', label: 'Line Details', icon: <IconSystem.eye size={18} />, onClick: () => { setSelectedLine(line); setDetailsOpen(true); } },
    ];
    const isDeposit = line.crAmount > 0;
    if (line.matchStatus === 'UNMATCHED') {
      if (canUpdate) actions.push({ id: 'match', label: 'Match…', icon: <IconSystem.link size={18} />, onClick: () => openMatch([line]) });
      if (canCreate) {
        if (isDeposit) {
          actions.push({ id: 'post-rv', label: 'Post Receipt Voucher…', icon: <IconSystem.add size={18} />, onClick: () => setPostRvLine(line) });
          actions.push({ id: 'quick', label: 'Quick Entry (interest / other income)…', icon: <IconSystem.add size={18} />, onClick: () => setQuickLine(line) });
          actions.push({ id: 'create-receipt', label: 'Create Receipt (Treasury)', icon: <IconSystem.add size={18} />, onClick: () => { setSelectedLine(line); setCreateReceiptOpen(true); } });
        } else {
          actions.push({ id: 'post-pv', label: 'Post Payment Voucher…', icon: <IconSystem.add size={18} />, onClick: () => setPostPvLine(line) });
          actions.push({ id: 'quick', label: 'Quick Entry (bank charges / direct debit)…', icon: <IconSystem.add size={18} />, onClick: () => setQuickLine(line) });
        }
      }
      if (canUpdate) actions.push({ id: 'ignore', label: 'Ignore', icon: <IconSystem.eyeOff size={18} />, onClick: () => { setSelectedLine(line); setIgnoreOpen(true); }, variant: 'danger' });
    } else if (MATCHED_STATES.includes(line.matchStatus)) {
      if (line.matchId) {
        actions.push({ id: 'view-match', label: 'View Match / Unmatch', icon: <IconSystem.link size={18} />, onClick: () => { setSelectedLine(line); setMatchDetailsOpen(true); } });
      } else if (canUpdate) {
        actions.push({ id: 'unmatch', label: 'Unmatch', icon: <IconSystem.xCircle size={18} />, onClick: () => setReopenLine(line), variant: 'danger' });
      }
      if (line.matchedReceiptId) actions.push({ id: 'view-receipt', label: 'View Receipt', icon: <IconSystem.file size={18} />, onClick: () => handleViewReceipt(line) });
    } else if (line.matchStatus === 'IGNORED' && canUpdate) {
      actions.push({ id: 'reopen', label: 'Reopen', icon: <IconSystem.refresh size={18} />, onClick: () => setReopenLine(line) });
    }
    return actions;
  }, [canUpdate, canCreate, handleViewReceipt, openMatch]);

  // Several unmatched lines of one direction can be matched to ONE book entry together.
  const selection = useMemo(() => {
    const open = selectedRows.filter(l => l.matchStatus === 'UNMATCHED');
    const dirs = new Set(open.map(lineDirection));
    return { open, sameDirection: dirs.size === 1, total: open.reduce((s, l) => s + lineAmount(l), 0) };
  }, [selectedRows]);

  const columns: SyutGridColumn<BankStatementLineDto>[] = useMemo(() => [
    {
      id: 'txnDate', header: 'Date', accessorKey: 'txnDate', size: 105,
      cell: ({ row }) => fmtDate((row.original as BankStatementLineDto).txnDate),
    },
    {
      id: 'description', header: 'Description', accessorKey: 'description', size: 260,
      cell: ({ row }) => (row.original as BankStatementLineDto).description || '—',
    },
    {
      id: 'reference', header: 'Reference', accessorKey: 'reference', size: 140,
      cell: ({ row }) => (row.original as BankStatementLineDto).reference || '—',
    },
    {
      id: 'drAmount', header: 'Withdrawal', accessorKey: 'drAmount', size: 110,
      cell: ({ row }) => {
        const l = row.original as BankStatementLineDto;
        return <span style={{ display: 'block', textAlign: 'right', fontWeight: l.drAmount ? 600 : 400 }}>{l.drAmount ? money(l.drAmount) : ''}</span>;
      },
    },
    {
      id: 'crAmount', header: 'Deposit', accessorKey: 'crAmount', size: 110,
      cell: ({ row }) => {
        const l = row.original as BankStatementLineDto;
        return <span style={{ display: 'block', textAlign: 'right', fontWeight: l.crAmount ? 600 : 400 }}>{l.crAmount ? money(l.crAmount) : ''}</span>;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'matchStatus', size: 120,
      cell: ({ row }) => matchStatusBadge((row.original as BankStatementLineDto).matchStatus),
    },
    {
      id: 'matchedWith', header: 'Matched With / Remarks', size: 200,
      cell: ({ row }) => {
        const l = row.original as BankStatementLineDto;
        return l.matchedEntries ? <strong>{l.matchedEntries}</strong> : <span style={muted}>{l.matchedReceiptNo || l.matchRemarks || '—'}</span>;
      },
    },
  ], []);

  if (!statement) return null;
  const bankLedgerId = (detail ?? statement).bankLedgerId;
  const toPrefill = (l: BankStatementLineDto): StatementLinePrefill => ({
    lineId: l.id, date: l.txnDate.slice(0, 10), bankLedgerId,
    bankLedgerLabel: (detail ?? statement).bankLedgerName || '', amount: lineAmount(l),
    reference: l.reference, description: l.description,
  });

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        {selection.open.length >= 2 && canUpdate && (
          <div style={{ ...band, margin: '0 0 0.5rem' }}>
            <span><strong>{selection.open.length}</strong> unmatched lines selected · total <strong>{money(selection.total)}</strong></span>
            {selection.sameDirection
              ? <button className="btn btn-primary" style={{ fontSize: '0.78rem', padding: '0.25rem 0.7rem' }} onClick={() => openMatch(selection.open)}>
                  <IconSystem.link size={14} /> Match together to one entry…
                </button>
              : <span style={{ color: '#d97706' }}>Select only deposits or only withdrawals to match them together.</span>}
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <SyutGrid data={lines} columns={columns}
            enableRowSelection onSelectionChange={setSelectedRows}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={page - 1} pageSize={pageSize}
            onPageChange={(idx) => setPage(idx + 1)}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            rowActions={getRowActions}
            isLoading={loading}
            emptyMessage={loading ? 'Loading...' : 'No lines match the current filters.'}
            frozenRightColumns={['actions']} />
        </div>
      </div>

      <LineDetailsModal isOpen={detailsOpen} line={selectedLine}
        onClose={() => { setDetailsOpen(false); setSelectedLine(null); }} />

      <ReceiptViewModal isOpen={viewReceipt !== null} receipt={viewReceipt}
        onClose={() => setViewReceipt(null)} />

      <MatchLinesModal isOpen={matchOpen} lines={matchLines} bankLedgerId={bankLedgerId}
        onMatched={refreshAll}
        onClose={() => { setMatchOpen(false); setMatchLines([]); }} />

      <MatchDetailsModal isOpen={matchDetailsOpen} line={selectedLine} canUpdate={canUpdate}
        onUnmatched={refreshAll}
        onClose={() => { setMatchDetailsOpen(false); setSelectedLine(null); }} />

      <IgnoreLineDialog isOpen={ignoreOpen} line={selectedLine}
        onIgnored={refreshAll}
        onClose={() => { setIgnoreOpen(false); setSelectedLine(null); }} />

      <ReceiptFormModal isOpen={createReceiptOpen}
        titleOverride="Create Receipt from Statement Line"
        prefill={selectedLine ? {
          receiptDate: (selectedLine.txnDate || '').slice(0, 10),
          bankLedgerId,
          bankLedgerLabel: (detail ?? statement).bankLedgerName || '',
          amount: selectedLine.crAmount,
          payerText: selectedLine.description || undefined,
          reference: selectedLine.reference || undefined,
          narration: selectedLine.description || undefined,
        } : undefined}
        lockedFields={['receiptDate', 'bankLedgerId', 'amount']}
        onSubmitOverride={handleCreateReceiptSubmit}
        onSaved={refreshAll}
        onClose={() => { setCreateReceiptOpen(false); setSelectedLine(null); }} />

      <QuickEntryDialog isOpen={quickLine !== null} line={quickLine}
        onPosted={refreshAll} onClose={() => setQuickLine(null)} />

      <ReceiptVoucherFormModal isOpen={postRvLine !== null}
        fromStatementLine={postRvLine ? toPrefill(postRvLine) : undefined}
        onSubmitOverride={async (payload) => { if (postRvLine) await bankStatementService.postReceiptVoucher(postRvLine.id, payload); }}
        onPosted={refreshAll} onClose={() => setPostRvLine(null)} />

      <PaymentVoucherFormModal isOpen={postPvLine !== null}
        fromStatementLine={postPvLine ? toPrefill(postPvLine) : undefined}
        onSubmitOverride={async (payload) => { if (postPvLine) await bankStatementService.postPaymentVoucher(postPvLine.id, payload); }}
        onPosted={refreshAll} onClose={() => setPostPvLine(null)} />

      <ConfirmDialog open={reopenLine !== null} title={reopenLine?.matchStatus === 'IGNORED' ? 'Reopen Line' : 'Unmatch Line'}
        message={reopenLine?.matchStatus === 'IGNORED'
          ? 'Reopen this ignored line so it can be matched?'
          : `Unmatch this line${reopenLine?.matchedReceiptNo ? ` from receipt ${reopenLine.matchedReceiptNo}` : ''}? It goes back to unmatched.`}
        confirmLabel={reopenLine?.matchStatus === 'IGNORED' ? 'Reopen' : 'Unmatch'} cancelLabel="Cancel" variant="danger"
        loading={reopening} loadingMessage="Saving…"
        onConfirm={handleConfirmReopen} onCancel={() => setReopenLine(null)} />
    </>
  );
};

export default ReconciliationWorksheet;
